import React, { Fragment } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { getAFDataInterfaces, getAllAFGroups } from 'data/accessFunction/accessFunction.actions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TargetModel, TargetModelAfCCC, TargetModelAfCCCSettings } from 'data/target/target.types';
import { TargetDefaultAFSelection } from 'data/customization/customization.types';
import i18n from 'i18n';
import { CaseModel } from 'data/case/case.types';
import { getFirstTargetTag } from 'xcipio/devices/forms/combineTargetsWithSameTypeAndValue';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import {
  getTargetDeliveryTemplateAccessFunctionList,
} from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { allowedTargetsToSetOptions } from 'xcipio/discovery-xcipio-selectors';
import AccessFunctions from './accessFunctions';
import { AppToaster, showErrors } from '../../../shared/toaster';
import { applyFieldValidators } from '../../../shared/form/form-validation';
import TargetSummaryOfTargetPanel from './targetSummaryOfTargetPanel';
import TargetCCCInfo from '../targetInfo/target-ccc-info';
import TargetCCCInfoNortelMtxCCR from '../targetInfo/target-ccc-nortel-mtx-ccr';
import AxsgpTargetInfo from '../targetInfo/axsgp-target-info';
import { afSelectionRequired } from './targetPanel.utils';
import { getTargetShouldHaveAFAssociationList, isAdditionalTargetInfoValid, validateTargetCCC } from '.';
import {
  TargetInfoHashItem, TARGET_INFO_TYPE,
} from '../targetInfo/target-info-defs';
import { getTargetInfoHashItem, createDefaultTargetInfoHashItem, trackTargetInfoUpdate } from '../targetInfo/util';

import './targetPanel.scss';
import VerintStarGateTargetInfo from '../targetInfo/VerintStarGateTargetInfo';
import { isVerintStarGateTargetInfoSupported } from '../targetInfo/VerintStarGateTargetInfo/utils';
import { isEricssonNETargetInfoSupported } from '../targetInfo/EricssonNETargetInfo/utils';
import EricssonNETargetInfo from '../targetInfo/EricssonNETargetInfo';
import GenbandC20TargetCCC from '../targetInfo/GenbandC20TargetCCC';
import UtimacoLIMSTargetCCC from '../targetInfo/UtimacoLIMSTargetCCC';
import RibbonC20CSCTargetCCC from '../targetInfo/RibbonC20CSCTargetCCC';

const dismissErrors = (errorkey: string) => {
  AppToaster.dismiss(errorkey);
};

const validTargetCCC = (targets: any, warrantCase: any, targetAfDetails: any, errors: any[],
  isSurvOptCombined: any) => {
  return validateTargetCCC(targets, targetAfDetails, isSurvOptCombined,
    warrantCase.type, errors, i18n.t('warrant.case.form.caseName', { index: warrantCase.caseIndex }), warrantCase);
};

const validTargets = (caseId: string, targets: any, targetFields: any, errors: Array<string>) => {
  let targetValidationResult = true;
  Object.entries(targetFields).forEach(([key, value]: any, index: number) => {
    const { primaryType, primaryValue } = targets[index];
    const { validation } = value.subFields.primaryValue;
    const field = value.subFields.primaryValue;

    if (validation.required && key === primaryType) {
      // Since this panel is for create at this time, there is no lastSavedValue.
      const lastSavedValue = null;
      const fieldValidationResult = applyFieldValidators(field.validation, primaryValue, lastSavedValue);
      targetValidationResult = targetValidationResult && fieldValidationResult.isValid;
      if (!fieldValidationResult.isValid && fieldValidationResult.errorMessage) {
        errors.push(`${caseId}${key}: ${fieldValidationResult.errorMessage}`);
      } else if (!fieldValidationResult.isValid && fieldValidationResult.errorMessage === '') {
        const validators = field.validators ? field.validators : [];
        validators.forEach((validator: any) => {
          errors.push(`${caseId}${key}: ${validator.errorMessage}`);
        });
      }
    }
    if (errors.length > 0) { showErrors(errors); }
  });
  return targetValidationResult;
};

export const handleAFandTargetCCCValidation = (
  targets: any,
  targetAfDetails: any,
  errorKey: string,
  warrantCase: CaseModel,
  errors: any[],
  isSurvOptCombined: any,
  targetDefaultAFSelection: TargetDefaultAFSelection | undefined,
) => {
  let targetShouldHaveAFAssociation: string[] = [];
  if (targetAfDetails != null) {
    targetShouldHaveAFAssociation = getTargetShouldHaveAFAssociationList(targetAfDetails);
  }
  // targetAfDetails that do not need an AFWT has the target.afs.afIds[i].bAFAssociation = false.
  // The GUI used bAFAssociation to determine whether to display the AF for a target.
  // Since all AFs are selected by default, targets that do not need an AFWT will have AFs.
  // Those AFs will be removed right before the warrant is to be created.
  const afCounts = targets.map((target: any) => {
    // For targets that do not need AF association, fakes AF count.
    const value = target.secondaryType ? `${target.primaryType}+${target.secondaryType}` : target.primaryType;
    if (targetShouldHaveAFAssociation.includes(value) === false) return 100;

    // If target does not require AF, fake AF count
    if (afSelectionRequired(targetDefaultAFSelection, warrantCase, target.primaryType) === false) return 100;

    // If the target value is empty dont validate AFs
    if (!target.primaryValue) return -1;
    const afCount = target.afs && target.afs.afIds ? target.afs.afIds.length : 0;
    const afGCount = target.afs && target.afs.afGroups ? target.afs.afGroups.length : 0;
    const afawGCount = target.afs && target.afs.afAutowireGroups
      ? target.afs.afAutowireGroups.length : 0;
    const afTCount = target.afs && target.afs.afAutowireTypes
      ? target.afs.afAutowireTypes.length : 0;
    return afCount + afGCount + afTCount + afawGCount;
  });

  const caseId = warrantCase.name;

  if (afCounts.includes(0)) {
    errors.push(i18n.t('warrant.form.fieldError.targetMissingAf', { caseId }));
  }

  validTargetCCC(targets, warrantCase, targetAfDetails, errors, isSurvOptCombined);
  dismissErrors(errorKey);
  return true;
};

// handle cases targets validation
export const handleTargetsValidation = (
  targets: any,
  targetAfDetails: any,
  errorKey: string, warrantCase: any,
  errors: any[], targetFields: any,
  isSurvOptCombined: any,
  targetDefaultAFSelection: TargetDefaultAFSelection | undefined,
) => {
  let targetValidationResult = true;
  const caseId = warrantCase.name;
  targetValidationResult = targetValidationResult &&
    validTargets(caseId, targets, targetFields, errors);
  targetValidationResult = targetValidationResult &&
    handleAFandTargetCCCValidation(targets, targetAfDetails, errorKey,
      warrantCase, errors, isSurvOptCombined, targetDefaultAFSelection);
  return targetValidationResult;
};

export class TargetPanel extends React.Component<any, any> {
  private errorKey = 'TargetPanel';

  constructor(props: any) {
    super(props);

    const {
      targets, hideEmptyTargets, currentStateMode,
    } = props;

    const selectedTarget = TargetPanel.selectTarget(targets, hideEmptyTargets);

    this.state = {
      fieldDirty: false,
      currentStateMode,
      targets,
      selectedAfs: selectedTarget,
      selectedTargetId: selectedTarget.id || '',
      selectedTargetType: selectedTarget.primaryType || '',
      selectedTargetAf: null,
    };
  }

  static selectTarget(targets: TargetModel[], hideEmptyTargets: boolean): Partial<TargetModel> {

    if (!targets?.length) {
      return {};
    }

    if (!hideEmptyTargets) {
      return targets[0];
    }

    return targets.find((target) => target.primaryValue != null && target.primaryValue !== '') || {};
  }

  componentDidMount = async () => {
    const { selectedDF } = this.props;
    const response = await this.props.getAllAFGroups(selectedDF === undefined ? '' : selectedDF);
    this.props.getTargetDeliveryTemplateAccessFunctionList(selectedDF);
    this.setState({ allAfGroups: response.action.payload.data.data });
  }

  isTargetsArrayIdEqual = (targetsA: any, targetsB: any) => {
    const targetsIDarrayA = targetsA.map((target: any) => target.id);
    const targetsIDarrayB = targetsB.map((target: any) => target.id);
    const intersection = targetsIDarrayA.filter((e: any) => targetsIDarrayB.indexOf(e) === -1);
    return intersection.length > 0;
  }

  componentDidUpdate = (prevProps: any) => {
    if (this.isTargetsArrayIdEqual(prevProps.targets, this.props.targets)) {
      dismissErrors(this.errorKey);

      const { targets, hideEmptyTargets, currentStateMode } = this.props;

      const selectedTarget = TargetPanel.selectTarget(targets, hideEmptyTargets);

      this.setState({
        fieldDirty: false,
        currentStateMode,
        targets,
        selectedAfs: selectedTarget,
        selectedTargetId: selectedTarget.id || '',
        selectedTargetType: selectedTarget.primaryType || '',
        selectedTargetAf: null,
      });
    }
  }

  newFieldUnselectTargetAf = (selectedTargetAf: any, newFields: any) => {
    const curselectedTargetAf =
      newFields.afs.afIds.filter((af: any) => af.id === selectedTargetAf.id);
    return curselectedTargetAf.length === 0;
  }

  handleAccessFunctionUpdate = (fieldDirty: boolean, newFields: any) => {
    dismissErrors(this.errorKey);
    const newTargets = JSON.parse(JSON.stringify(this.state.targets));
    const index = newTargets.findIndex((nt: any) => nt.id === newFields.id);
    newTargets[index] = { ...newFields };
    if (this.state.selectedTargetAf &&
      this.newFieldUnselectTargetAf(this.state.selectedTargetAf, newFields)) {
      this.setState({
        fieldDirty, selectedAfs: newFields, targets: newTargets, selectedTargetAf: null,
      }, () => {
        this.props.handleSubmit(this.state.targets);
      });
    } else {
      this.setState({ fieldDirty, selectedAfs: newFields, targets: newTargets }, () => {
        this.props.handleSubmit(this.state.targets);
      });
    }
  }

  handleTargetcccClick = async (afDetails: any) => {
    const { selectedDF } = this.props;
    const response = await getAFDataInterfaces(selectedDF === undefined ? '' : selectedDF, afDetails.id).payload;
    // *** create default TargetInfo if there isn't one exist.
    // look for the target
    let newTargetInfoHashItem: TargetInfoHashItem | null = null;
    const targetIndex: number = this.state.targets.findIndex((target: any) =>
      (target.id === this.state.selectedAfs.id));
    // look for the target info for the AF type
    const targetInfoHashIndex: number = this.state.targets[targetIndex].targetInfoHash ?
      this.state.targets[targetIndex].targetInfoHash.findIndex((targetInfoHashItem: any) =>
        (targetInfoHashItem.targetInfoType === afDetails.x3TargetAssociations)) : -1;
    // if there isn't a target info for the AF type, create a default one
    if (targetInfoHashIndex === -1 ||
      !this.state.targets[targetIndex].targetInfoHash[targetInfoHashIndex].targetInfos ||
      this.state.targets[targetIndex].targetInfoHash[targetInfoHashIndex].targetInfos.length <= 0) {
      const { selectedCaseIndex, cases } = this.props;
      const caseServices = selectedCaseIndex >= 0 && cases ? cases[selectedCaseIndex].services : null;
      newTargetInfoHashItem =
        createDefaultTargetInfoHashItem(afDetails, afDetails.x3TargetAssociations, this.state.selectedTargetType,
          caseServices);
    }

    let newTargets: any[] | null = null;
    // if there is a new default target info, add to the corresponding target
    if (newTargetInfoHashItem) {
      newTargets = [...this.state.targets];
      newTargets[targetIndex] = {
        ...this.state.targets[targetIndex],
        targetInfoHash: !this.state.targets[targetIndex].targetInfoHash ? [newTargetInfoHashItem] :
          [...this.state.targets[targetIndex].targetInfoHash, newTargetInfoHashItem],
      };
      // Add to change tracker
      trackTargetInfoUpdate(this.props.changeTracker, newTargetInfoHashItem,
        this.props.warrantCase?.id, this.state.selectedTargetId, null);
    }

    const newState = {
      fieldDirty: newTargetInfoHashItem === null ? this.state.fieldDirty : true,
      selectedAfs: newTargetInfoHashItem === null || newTargets == null ?
        this.state.selectedAfs : newTargets[targetIndex],
      targets: newTargetInfoHashItem === null || newTargets == null ?
        this.state.targets : newTargets,
      selectedTargetAf: afDetails,
      afDIDetails: response.data.data,
    };

    // this.setState({ selectedTargetAf: afDetails, afDIDetails: response.data.data });
    this.setState(newState, () => {
      if (newTargetInfoHashItem != null) {
        this.props.handleSubmit(this.state.targets);
      }
    });
  }

  handleTargetcccUpdate = (afSettings: TargetModelAfCCCSettings[]) => {
    const { onCasePanelChanged } = this.props;
    const {
      selectedTargetAf,
      selectedAfs,
    } = this.state;

    const afType = selectedTargetAf.type;
    const afId = selectedTargetAf.id;

    const afCCC: TargetModelAfCCC = {
      afType,
      afSettings,
    };

    const clonedSelectedAfs = cloneDeep(selectedAfs);

    const previousAfCCC: TargetModelAfCCC | null =
      clonedSelectedAfs.afs?.afccc?.find((af: any) => af.afType === afType);

    if (previousAfCCC == null) {
      // There isn't a setting from this selected type, so it can be added directly
      clonedSelectedAfs.afs.afccc.push(afCCC);

    } else {
      // Remove remove existing ones and add the new ones
      previousAfCCC.afSettings = previousAfCCC.afSettings.filter(({ afids }) => !afids.includes(afId));
      previousAfCCC.afSettings.push(...afSettings);
    }

    const preValueTarget = this.state.targets
      .find((nt: any) => nt.id === this.state.selectedTargetId);
    const preValue = preValueTarget && preValueTarget.afs && preValueTarget.afs.afccc
      ? preValueTarget.afs.afccc : undefined;
    const newTargets = JSON.parse(JSON.stringify(this.state.targets));
    const index = newTargets.findIndex((nt: any) => nt.id === this.state.selectedTargetId);
    newTargets[index] = { ...clonedSelectedAfs };

    this.setState({ fieldDirty: true, selectedAfs: clonedSelectedAfs, targets: newTargets }, () => {
      if (!(this.state.selectedTargetAf?.tag === 'GENBANDC20')) {
        this.props.handleSubmit(this.state.targets);
      }
    });

    // Add to change tracker
    if (this.props.changeTracker && this.state.selectedTargetId && selectedTargetAf.type) {
      this.props.changeTracker.addUpdated(`case.${this.props.warrantCase.id}.target.${this.state.selectedTargetId}.afs.afccc.${selectedTargetAf.type}`,
        afCCC, null, preValue);
    }

    if (onCasePanelChanged != null) {
      onCasePanelChanged();
    }

    if (this.state.selectedTargetAf?.tag === 'GENBANDC20') {
      return newTargets;
    }
  }

  handleTargetInfoUpdate = (newTargetInfoHashItem: TargetInfoHashItem) => {
    const { onCasePanelChanged } = this.props;

    const index = this.state.targets
      .findIndex((nt: any) => nt.id === this.state.selectedTargetId);

    const preValueTarget = this.state.targets[index];
    let preValue: TargetInfoHashItem | null = null;

    const newTargets = [...this.state.targets];
    newTargets[index] = cloneDeep(preValueTarget);
    let bfound = false;
    if (newTargets[index].targetInfoHash) {
      newTargets[index].targetInfoHash.some((targetInfoHashItem: TargetInfoHashItem, i: number) => {
        if (targetInfoHashItem.targetInfoType === newTargetInfoHashItem.targetInfoType) {
          preValue = newTargets[index].targetInfoHash[i];
          newTargets[index].targetInfoHash[i] = newTargetInfoHashItem;
          bfound = true;
          return true;
        }
        return false;
      });
    }
    if (bfound === false) {
      if (newTargets[index].targetInfoHash) {
        newTargets[index].targetInfoHash.push(newTargetInfoHashItem);
      } else {
        newTargets[index].targetInfoHash = [newTargetInfoHashItem];
      }
    }
    // Add to change tracker
    trackTargetInfoUpdate(
      this.props.changeTracker,
      newTargetInfoHashItem,
      this.props.warrantCase?.id,
      this.state.selectedTargetId,
      preValue,
    );

    this.setState({ fieldDirty: true, selectedAfs: newTargets[index], targets: newTargets },
      () => {
        if (!(this.state.selectedTargetAf?.tag === 'GENBANDC20')) {
          this.props.handleSubmit(this.state.targets);
        }
      });

    if (onCasePanelChanged != null) {
      onCasePanelChanged();
    }

    if (this.state.selectedTargetAf?.tag === 'GENBANDC20') {
      return this.state.targets;
    }
  }

  isAccessFunctionTargetDetailValid = (afDetails: any): boolean => {
    const {
      warrantCase,
      deployment
    } = this.props;

    const {
      targets,
      selectedAfs,
    } = this.state;

    const isSurvOptCombined = warrantCase?.options?.circuitSwitchedVoiceCombined;
    const target = targets.find((entry: any) => (entry.id === selectedAfs.id));
    const allSelectedAfCCC = target.afs.afccc;

    const result = isAdditionalTargetInfoValid(
      afDetails,
      [afDetails],
      allSelectedAfCCC,
      target,
      isSurvOptCombined ?? false,
      warrantCase.type,
      [],
      '',
      warrantCase
    );

    return result;
  }

  handleTargetClick = (target: any) => { // handle target item click
    this.setState({
      selectedAfs: target,
      selectedTargetId: target.id,
      selectedTargetType: target.primaryType,
      selectedTargetAf: undefined,
    });
  }

  renderVerintTargetInfo(): JSX.Element | null {
    const {
      selectedAfs,
      selectedTargetAf,
      currentStateMode,
    } = this.state;

    if (!isVerintStarGateTargetInfoSupported(selectedTargetAf)) {
      return null;
    }

    const afType = selectedTargetAf.type;
    const afId = selectedTargetAf.id;

    const afCCC: TargetModelAfCCC | null = selectedAfs.afs?.afccc?.find((af: any) => af.afType === afType);

    let accessFunctionSettings: TargetModelAfCCCSettings[] = [];
    if (afCCC != null) {
      accessFunctionSettings = afCCC.afSettings.filter(({ afids }) => afids.includes(afId));
    }

    const isEditing = currentStateMode !== CONFIG_FORM_MODE.View;

    return (
      <VerintStarGateTargetInfo
        targetAccessFunction={selectedTargetAf}
        accessFunctionSettings={accessFunctionSettings}
        onUpdate={this.handleTargetcccUpdate}
        isEditing={isEditing}
      />
    );
  }

  renderEricssonNETargetInfo(): JSX.Element | null {
    const {
      selectedAfs,
      selectedTargetAf,
      currentStateMode,
    } = this.state;

    if (!isEricssonNETargetInfoSupported(selectedTargetAf)) {
      return null;
    }

    const afType = selectedTargetAf.type;
    const afId = selectedTargetAf.id;

    const afCCC: TargetModelAfCCC | null = selectedAfs.afs?.afccc?.find((af: any) => af.afType === afType);

    let accessFunctionSettings: TargetModelAfCCCSettings[] = [];
    if (afCCC != null) {
      accessFunctionSettings = afCCC.afSettings.filter(({ afids }) => afids.includes(afId));
    }

    const isEditing = currentStateMode !== CONFIG_FORM_MODE.View;

    return (
      <EricssonNETargetInfo
        interceptType={this.props.warrantCase.type}
        selectedTargetType={this.state.selectedTargetType}
        targetAccessFunction={selectedTargetAf}
        accessFunctionSettings={accessFunctionSettings}
        onUpdate={this.handleTargetcccUpdate}
        isEditing={isEditing}
      />
    );
  }

  render() {

    const {
      onCasePanelChanged, targetDeliveryTemplates, targetDeliveryTemplatesMap, targetDomainList,
    } = this.props;

    const labelValue = this.state.selectedTargetAf && this.state.selectedTargetAf.name
      ? i18n.t('warrant.target.form.additionalTargetInfoFor', { name: this.state.selectedTargetAf.name }) : i18n.t('warrant.target.form.additionalTargetInfo');
    const isContent = this.props.warrantCase
      && (this.props.warrantCase.type === 'CC' || this.props.warrantCase.type === 'ALL');
    return (
      <Fragment>
        <div className="targetContainer">
          <div className="targetContainerDiv">
            <TargetSummaryOfTargetPanel
              targets={this.props.targets}
              currentStateMode={this.state.currentStateMode}
              selectedTarget={this.state.selectedAfs}
              targetFields={this.props.targetFields}
              caseDetailsFieldUpdateHandler={this.props.caseDetailsFieldUpdateHandler}
              shouldTargetsUpdate={this.props.shouldTargetsUpdate}
              warrantCase={this.props.warrantCase}
              changeTracker={this.props.changeTracker}
              handleClick={this.handleTargetClick}
              hideEmptyTargets={this.props.hideEmptyTargets}
              targetDomainList={targetDomainList}
              allowedTargetsToSetOptions={this.props.allowedTargetsToSetOptions}
            />
          </div>
          <div className="targetContainerDiv">
            <AccessFunctions
              selectedAfs={this.state.selectedAfs}
              currentStateMode={this.state.currentStateMode}
              allAfGroups={this.state.allAfGroups}
              warrantCase={this.props.warrantCase}
              targetId={this.state.selectedTargetId}
              afDetails={this.state.selectedTargetAf}
              accessFunctionUpdateSet={this.handleAccessFunctionUpdate}
              targetAfDetails={this.props.targetAfDetails}
              targetType={this.state.selectedTargetType}
              targetTypeLabel={getFirstTargetTag(this.state.selectedTargetType) ?? this.state.selectedTargetType}
              drvirtual={this.props.drvirtual}
              onTargetcccClick={this.handleTargetcccClick}
              isAccessFunctionTargetDetailValid={this.isAccessFunctionTargetDetailValid}
              onCasePanelChanged={onCasePanelChanged}
            />
          </div>
          {this.state.selectedTargetAf && (
            <div className="targetContainerDiv additional-target-info">
              <div className="section-header">
                {labelValue}
              </div>
              <p className="instruction-text">
                {i18n.t('warrant.target.form.enterRequiredInfo')}
              </p>
              <div className="additional-target-info-forms">
                {this.renderVerintTargetInfo()}
                {this.renderEricssonNETargetInfo()}
                {AxsgpTargetInfo.isSupported(this.state.selectedTargetAf) && (
                  <AxsgpTargetInfo
                    afDetails={this.state.selectedTargetAf}
                    targetType={this.state.selectedTargetType}
                    handleUpdate={this.handleTargetInfoUpdate}
                    targetInfoHashItem={AxsgpTargetInfo.getTargetInfoHashItem(
                      this.state.selectedAfs,
                    )}
                  />
                )}
                {TargetCCCInfo.isSupportedAFType(this.state.selectedTargetAf) && (
                  <Fragment>
                    <TargetCCCInfo
                      currentStateMode={this.state.currentStateMode}
                      targetId={this.state.selectedTargetId}
                      caseId={this.props.warrantCase.id}
                      afDetails={this.state.selectedTargetAf}
                      targetType={this.state.selectedTargetType}
                      handleUpdate={this.handleTargetcccUpdate}
                      selectedAfs={this.state.selectedAfs}
                      afDIDetails={this.state.afDIDetails}
                      afid={this.state.selectedTargetAf ?
                        this.state.selectedTargetAf.id : undefined}
                    />

                    {isContent && this.state.selectedAfs && this.state.selectedAfs.afs &&
                      this.state.selectedTargetAf.x3TargetAssociations &&
                      this.state.selectedTargetAf.x3TargetAssociations != null
                      ? (
                        <TargetCCCInfoNortelMtxCCR
                          name={this.state.selectedTargetAf.id}
                          selectedTargetAf={this.state.selectedTargetAf}
                          isSurvOptCombined={this.props.warrantCase.options
                            && this.props.warrantCase.options.circuitSwitchedVoiceCombined}
                          handleFieldUpdate={this.handleTargetInfoUpdate}
                          targetInfoHashItem={getTargetInfoHashItem(this.state.selectedAfs,
                            TARGET_INFO_TYPE.MTXCFCI)}
                          currentStateMode={this.state.currentStateMode}
                          targetId={this.state.selectedTargetId}
                          afDetails={this.state.selectedTargetAf}
                          caseId={this.props.warrantCase.id}
                          afid={this.state.selectedTargetAf ?
                            this.state.selectedTargetAf.id : undefined}
                        />
                      )
                      : <Fragment />}
                  </Fragment>
                )}
                {this.state.selectedTargetAf.tag === 'GENBANDC20' ? (
                  <GenbandC20TargetCCC
                    afDetails={this.state.selectedTargetAf}
                    handleUpdate={this.handleTargetcccUpdate}
                    handleTargetInfoHashItemUpdate={this.handleTargetInfoUpdate}
                    targetInfoHashItem={getTargetInfoHashItem(this.state.selectedAfs, TARGET_INFO_TYPE.NTILITARGETINFO)}
                    afid={this.state.selectedTargetAf ? this.state.selectedTargetAf.id : undefined}
                    handleSubmit={this.props.handleSubmit}
                    targetId={this.state.selectedTargetId}
                    caseId={this.props.warrantCase.id}
                    targetType={this.state.selectedTargetType}
                    selectedAfs={this.state.selectedAfs}
                    afDIDetails={this.state.afDIDetails}
                    warrantCase={this.props.warrantCase}
                    currentStateMode={this.state.currentStateMode}
                    targetDeliveryTemplates={targetDeliveryTemplates}
                    targetDeliveryTemplatesMap={targetDeliveryTemplatesMap}
                  />
                )
                  :
                  <Fragment />}
                {this.state.selectedTargetAf.tag === 'SIEMENS_LIMS' ? (
                  <UtimacoLIMSTargetCCC
                    afDetails={this.state.selectedTargetAf}
                    handleUpdate={this.handleTargetcccUpdate}
                    handleTargetInfoHashItemUpdate={this.handleTargetInfoUpdate}
                    targetInfoHashItem={getTargetInfoHashItem(this.state.selectedAfs, TARGET_INFO_TYPE.NTILITARGETINFO)}
                    afid={this.state.selectedTargetAf ? this.state.selectedTargetAf.id : undefined}
                    handleSubmit={this.props.handleSubmit}
                    selectedTargetAf={this.state.selectedTargetAf}
                    targetId={this.state.selectedTargetId}
                    caseId={this.props.warrantCase.id}
                    targetType={this.state.selectedTargetType}
                    selectedAfs={this.state.selectedAfs}
                    afDIDetails={this.state.afDIDetails}
                    warrantCase={this.props.warrantCase}
                    currentStateMode={this.state.currentStateMode}
                  />
                )
                  :
                  <Fragment />}
                {this.state.selectedTargetAf.tag === 'RIBBONC20_CSC' ? (
                  <RibbonC20CSCTargetCCC
                    afDetails={this.state.selectedTargetAf}
                    handleUpdate={this.handleTargetcccUpdate}
                    handleTargetInfoHashItemUpdate={this.handleTargetInfoUpdate}
                    targetInfoHashItem={getTargetInfoHashItem(this.state.selectedAfs, TARGET_INFO_TYPE.NTILITARGETINFO)}
                    afid={this.state.selectedTargetAf ? this.state.selectedTargetAf.id : undefined}
                    handleSubmit={this.props.handleSubmit}
                    selectedTargetAf={this.state.selectedTargetAf}
                    targetId={this.state.selectedTargetId}
                    caseId={this.props.warrantCase.id}
                    targetType={this.state.selectedTargetType}
                    selectedAfs={this.state.selectedAfs}
                    afDIDetails={this.state.afDIDetails}
                    warrantCase={this.props.warrantCase}
                    currentStateMode={this.state.currentStateMode}
                  />
                )
                  :
                  <Fragment />}
              </div>
            </div>
          )}
        </div>
      </Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
  getAllAFGroups,
  getTargetDeliveryTemplateAccessFunctionList,
}, dispatch);

const mapStateToProps = ({
  cases: { cases, selectedIndex: selectedCaseIndex },
  warrants: {
    customizationconfig: { targetDefaultAFSelection },
  },
  targetDeliveryTemplateAccessFunction: { entities, targetDeliveryTemplatesMap },
  discoveryXcipio
}: any) => ({
  cases,
  selectedCaseIndex,
  targetDefaultAFSelection,
  targetDeliveryTemplates: entities,
  targetDeliveryTemplatesMap,
  allowedTargetsToSetOptions: allowedTargetsToSetOptions({ discoveryXcipio } as any)
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(TargetPanel);
